import React, { useState, useEffect } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { TextField, Popover, Button, Box, IconButton } from "@mui/material";
import { CalendarToday as CalendarTodayIcon } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import dayjs, { Dayjs } from "dayjs";
import { FormLabel } from "../utils";

const styles = makeStyles(() => ({
    root: {
        "& .MuiOutlinedInput-root": {
            borderRadius: "15px",
        },
        "& legend": { display: "none" },
        "& fieldset": { top: 0 },
        zIndex: 0,
    },
    calendarContainer: {
        padding: "16px",
        minWidth: "300px",
    },
    calendarHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "16px",
    },
    calendarGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(7, 1fr)",
        gap: "4px",
        marginBottom: "16px",
    },
    dayHeader: {
        textAlign: "center",
        fontWeight: "bold",
        padding: "8px",
        fontSize: "12px",
        color: "#666",
    },
    dayCell: {
        textAlign: "center",
        padding: "8px",
        cursor: "pointer",
        borderRadius: "4px",
        "&:hover": {
            backgroundColor: "#f0f0f0",
        },
    },
    selectedDay: {
        backgroundColor: "#1976d2",
        color: "white",
        "&:hover": {
            backgroundColor: "#1565c0",
        },
    },
    today: {
        border: "2px solid #1976d2",
    },
    otherMonth: {
        color: "#ccc",
    },
    timeInputs: {
        display: "flex",
        gap: "8px",
        marginBottom: "16px",
        alignItems: "center",
    },
    actionButtons: {
        display: "flex",
        justifyContent: "flex-end",
        gap: "8px",
        marginTop: "16px",
    },
}));

interface FTPDateTimePickerProps {
    name: string;
    label?: string;
    placeholder?: string;
    className?: string;
}

export const FTPDateTimePicker = ({
    name,
    label,
    placeholder,
    className,
    ...props
}: FTPDateTimePickerProps) => {
    const classes = styles();
    const {
        control,
        trigger,
        watch,
        formState: { errors },
    } = useFormContext();

    const formValue = watch(name);
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
    const [selectedTime, setSelectedTime] = useState({
        hours: dayjs().format("HH"),
        minutes: dayjs().format("mm"),
    });

    const open = Boolean(anchorEl);

    // Update selected date/time when form value changes
    useEffect(() => {
        if (formValue) {
            const date = dayjs(formValue);
            if (date.isValid()) {
                setSelectedDate(date);
                setSelectedTime({
                    hours: date.format("HH"),
                    minutes: date.format("mm"),
                });
            }
        }
    }, [formValue]);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
        // Initialize with current form value if exists
        if (formValue) {
            const date = dayjs(formValue);
            if (date.isValid()) {
                setSelectedDate(date);
                setSelectedTime({
                    hours: date.format("HH"),
                    minutes: date.format("mm"),
                });
            }
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleDayClick = (day: Dayjs) => {
        setSelectedDate(day);
    };

    const handleTimeChange = (type: "hours" | "minutes", value: string) => {
        setSelectedTime((prev) => ({
            ...prev,
            [type]: value,
        }));
    };

    const handleOk = (onChange: (value: string) => void) => {
        const dateTime = selectedDate
            .hour(parseInt(selectedTime.hours))
            .minute(parseInt(selectedTime.minutes))
            .second(0)
            .millisecond(0);

        // Format as datetime-local string (YYYY-MM-DDTHH:mm)
        const formattedValue = dateTime.format("YYYY-MM-DDTHH:mm");
        onChange(formattedValue);
        trigger(name);
        handleClose();
    };

    const handleCancel = () => {
        handleClose();
    };

    // Generate calendar days
    const generateCalendarDays = () => {
        const startOfMonth = selectedDate.startOf("month");
        const endOfMonth = selectedDate.endOf("month");
        const startOfCalendar = startOfMonth.startOf("week");
        const endOfCalendar = endOfMonth.endOf("week");

        const days: Dayjs[] = [];
        let current = startOfCalendar;

        while (current.isBefore(endOfCalendar) || current.isSame(endOfCalendar, "day")) {
            days.push(current);
            current = current.add(1, "day");
        }

        return days;
    };

    const calendarDays = generateCalendarDays();
    const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    return (
        <div className={`flex flex-col ${className || ""}`}>
            <FormLabel>{label}</FormLabel>
            <Controller
                name={name}
                control={control}
                render={({ field, fieldState: { error } }) => (
                    <div>
                        <TextField
                            placeholder={placeholder || label}
                            {...props}
                            {...field}
                            fullWidth
                            className={classes.root}
                            error={!!errors[`${name}`]}
                            InputProps={{
                                endAdornment: (
                                    <IconButton
                                        onClick={handleClick}
                                        edge="end"
                                        size="small"
                                        sx={{ marginRight: "8px" }}
                                    >
                                        <CalendarTodayIcon />
                                    </IconButton>
                                ),
                                readOnly: true,
                            }}
                            onBlur={(e) => {
                                trigger(name);
                                field.onBlur();
                                if (props.onBlur) props.onBlur(e);
                            }}
                        />
                        {error && (
                            <div style={{ color: "#d32f2f", fontSize: "0.75rem", marginTop: "4px" }}>
                                {error?.message}
                            </div>
                        )}
                        <Popover
                            open={open}
                            anchorEl={anchorEl}
                            onClose={handleClose}
                            anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "left",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "left",
                            }}
                        >
                            <Box className={classes.calendarContainer}>
                                {/* Month/Year Navigation */}
                                <Box className={classes.calendarHeader}>
                                    <IconButton
                                        size="small"
                                        onClick={() => setSelectedDate(selectedDate.subtract(1, "month"))}
                                    >
                                        ←
                                    </IconButton>
                                    <Box sx={{ fontWeight: "bold" }}>
                                        {selectedDate.format("MMMM YYYY")}
                                    </Box>
                                    <IconButton
                                        size="small"
                                        onClick={() => setSelectedDate(selectedDate.add(1, "month"))}
                                    >
                                        →
                                    </IconButton>
                                </Box>

                                {/* Calendar Grid */}
                                <Box className={classes.calendarGrid}>
                                    {weekDays.map((day) => (
                                        <Box key={day} className={classes.dayHeader}>
                                            {day}
                                        </Box>
                                    ))}
                                    {calendarDays.map((day) => {
                                        const isSelected = day.isSame(selectedDate, "day");
                                        const isToday = day.isSame(dayjs(), "day");
                                        const isOtherMonth = !day.isSame(selectedDate, "month");

                                        return (
                                            <Box
                                                key={day.format("YYYY-MM-DD")}
                                                className={`${classes.dayCell} ${isSelected ? classes.selectedDay : ""
                                                    } ${isToday && !isSelected ? classes.today : ""} ${isOtherMonth ? classes.otherMonth : ""
                                                    }`}
                                                onClick={() => handleDayClick(day)}
                                            >
                                                {day.format("D")}
                                            </Box>
                                        );
                                    })}
                                </Box>

                                {/* Time Inputs */}
                                <Box className={classes.timeInputs}>
                                    <TextField
                                        type="time"
                                        label="Time"
                                        value={`${selectedTime.hours}:${selectedTime.minutes}`}
                                        onChange={(e) => {
                                            const [hours, minutes] = e.target.value.split(":");
                                            if (hours) handleTimeChange("hours", hours);
                                            if (minutes) handleTimeChange("minutes", minutes);
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                        size="small"
                                        fullWidth
                                    />
                                </Box>

                                {/* Action Buttons */}
                                <Box className={classes.actionButtons}>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={handleCancel}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => handleOk(field.onChange)}
                                    >
                                        OK
                                    </Button>
                                </Box>
                            </Box>
                        </Popover>
                    </div>
                )}
            />
        </div>
    );
};

